import { type Dayjs } from 'dayjs';
import { type ReactElement, useMemo } from 'react';
import { EntityDailyHistoryPanel } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel';
import { resolveWindowDayKeys } from '@/features/shared/utils/chartDateWindow';
import { useTestHistoryPanelData } from '@/features/tests/pages/useTestHistoryPanelData';
import type { TestRunStatus } from '@flowqa/shared';

type TestHistoryDateWindow = {
  startDay: string | null;
  endDay: string | null;
  previousStartDay: string | null;
  previousEndDay: string | null;
};

export type TestsPageHistorySectionProps = {
  panelDateQuickFilter: string;
  panelDateWindow: TestHistoryDateWindow;
  panelFilterStatuses: TestRunStatus[];
  panelFilterSuites: string[];
  panelFilterTags: string[];
  panelFilterTests: string[];
  panelNoRowsText: string;
  panelSearchValue: string;
  dateRangeLabel: string | null;
  latestDateBounds: { from: Dayjs | null; to: Dayjs | null };
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  showForcedLoadingState?: boolean;
};

export const TestsPageHistorySection = ({
  panelDateQuickFilter,
  panelDateWindow,
  panelFilterStatuses,
  panelFilterSuites,
  panelFilterTags,
  panelFilterTests,
  panelNoRowsText,
  panelSearchValue,
  dateRangeLabel,
  latestDateBounds,
  selectedOrganisationId,
  selectedProjectId,
  showForcedLoadingState = false,
}: TestsPageHistorySectionProps): ReactElement => {
  const historyDayKeys = useMemo(
    () =>
      resolveWindowDayKeys({
        startDay: panelDateWindow.startDay,
        endDay: panelDateWindow.endDay,
        fallbackStartDay: latestDateBounds.from?.format('YYYY-MM-DD') ?? null,
        fallbackEndDay: latestDateBounds.to?.format('YYYY-MM-DD') ?? null,
        descending: true,
      }),
    [panelDateWindow.endDay, panelDateWindow.startDay, latestDateBounds.from, latestDateBounds.to],
  );
  const {
    dailyHistoryLoading,
    dailyHistoryRows,
    historyPage,
    historyPageSize,
    historySortDirection,
    historySortKey,
    showErrorState,
    setHistoryPage,
    setHistoryPageSize,
    setHistorySortDirection,
    setHistorySortKey,
    totalCount,
  } = useTestHistoryPanelData({
    panelDateQuickFilter,
    panelDateWindow,
    panelFilterStatuses,
    panelFilterSuites,
    panelFilterTags,
    panelFilterTests,
    panelSearchValue,
    latestDateBounds: {
      startDay: latestDateBounds.from?.format('YYYY-MM-DD') ?? null,
      endDay: latestDateBounds.to?.format('YYYY-MM-DD') ?? null,
    },
    selectedOrganisationId,
    selectedProjectId,
  });

  return (
    <EntityDailyHistoryPanel
      title="Test History"
      dateRangeLabel={dateRangeLabel}
      dayKeys={historyDayKeys}
      entityLabel="Test"
      activityLabel="runs"
      rows={dailyHistoryRows}
      loading={showForcedLoadingState || dailyHistoryLoading}
      showErrorState={!showForcedLoadingState && showErrorState}
      noRowsText={panelNoRowsText}
      defaultSortKey="prefixLabel"
      sortKey={historySortKey}
      sortDirection={historySortDirection}
      sortOptions={[
        { key: 'prefixLabel', label: 'Suite' },
        { key: 'name', label: 'Test' },
      ]}
      onSortChange={(nextSortKey, nextSortDirection) => {
        setHistorySortKey(nextSortKey);
        setHistorySortDirection(nextSortDirection);
        setHistoryPage(0);
      }}
      page={historyPage}
      pageSize={historyPageSize}
      totalCount={totalCount}
      onPageChange={setHistoryPage}
      onPageSizeChange={(nextPageSize) => {
        setHistoryPageSize(nextPageSize);
        setHistoryPage(0);
      }}
      nameColumnMinWidthPx={180}
      nameColumnDefaultWidthPx={360}
    />
  );
};
